import React from 'react'
import { FloatingInspectorButton, FloatingInspectorButtonProps, FloatingInspectorButtonHandle } from '../FloatingInspectorButton'
import { lightTheme } from '@/customTheme'

export interface FloatingInspectorButtonInputProps
  extends Pick<
    FloatingInspectorButtonProps,
    | 'onClick'
    | 'selected'
    | 'disabled'
    | 'showArrow'
    | 'style'
    | 'tooltipText'
    | 'placementTooltip'
    | 'marginTooltip'
    | 'isSecondaryStyle'
    | 'popoverContent'
  > {
  value: string | number
  inputType: 'text' | 'number'
  onChange: (value: string | number) => void
  onSubmit: (value: string | number, isUnmount?: boolean) => void
}

const FloatingInspectorButtonInput: React.FC<FloatingInspectorButtonInputProps> = ({
  value,
  inputType,
  onChange,
  onSubmit,
  onClick,
  selected,
  disabled,
  showArrow,
  style,
  tooltipText,
  placementTooltip,
  marginTooltip,
  isSecondaryStyle,
  popoverContent,
}) => {
  const inputRef = React.useRef<HTMLInputElement>(null)
  const buttonRef = React.useRef<FloatingInspectorButtonHandle>(null)

  // Ensure pending value is submitted if the input unmounts before blur fires
  React.useEffect(() => {
    const node = inputRef.current
    return () => {
      const currentValue = node?.value
      if (currentValue && verifyValue(currentValue)) {
        // Convert to number for numeric inputs to prevent string fontSize issues
        const valueToSubmit = inputType === 'number' ? Number(currentValue) : currentValue
        onSubmit(valueToSubmit, true)
      }
    }
  }, [])

  function onValueChange(value) {
    onChange(value)
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Enter') {
      onSubmitWithVerification(e.currentTarget.value)
      inputRef.current?.blur()
      if (popoverContent && buttonRef.current) {
        buttonRef.current.closePopover()
      }
    }
  }

  function verifyValue(value: string | number): boolean {
    if (inputType === 'number') {
      const cleaned = String(value).replace(/[^0-9.-]/g, '')
      if (!cleaned || /^[-.]$/.test(cleaned) || isNaN(Number(cleaned))) {
        return false
      }
      return true
    }
    return String(value).trim().length > 0
  }

  function onSubmitWithVerification(value) {
    if (verifyValue(value)) {
      // Convert to number for numeric inputs to prevent string fontSize issues
      const valueToSubmit = inputType === 'number' ? Number(value) : value
      onSubmit(valueToSubmit)
    }
  }

  return (
    <FloatingInspectorButton
      ref={buttonRef}
      onClick={onClick}
      selected={selected}
      disabled={disabled}
      showArrow={showArrow}
      style={{ ...style, minWidth: '26px'}}
      tooltipText={tooltipText}
      placementTooltip={placementTooltip}
      marginTooltip={marginTooltip}
      isSecondaryStyle={isSecondaryStyle}
      popoverContent={popoverContent}
    >
      <input
        ref={inputRef}
        type={inputType}
        value={value}
        onChange={e => onValueChange(e.currentTarget.value)}
        onKeyDown={onKeyDown}
        onBlur={e => onSubmitWithVerification(e.currentTarget.value)}
        onFocus={e => {
          setTimeout(() => {
            e.target.select()
          }, 100)
        }}
        style={{
          minWidth: '26px',
          width: 'fit-content',
          padding: 0,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          ...lightTheme.typography.Small12medium,
          color: disabled ? lightTheme.colors.grayScale200 : lightTheme.colors.blackGray,
          border: 'none',
          outline: 'none',
          background: 'transparent',
          textAlign: 'center',
        }}
      />
    </FloatingInspectorButton>
  )
}

export default FloatingInspectorButtonInput
